import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  useTheme
} from '@mui/material';
import Category from '@mui/icons-material/Category';
import CloseIcon from '@mui/icons-material/Close';
import { Material } from '../types';

interface MaterialFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: Material) => void;
  material: Material | null;   // empty for create, selected for update
  isEdit?: boolean;
}

const commonTextFieldStyles = {
  mb: 2,
  backgroundColor: 'white',
  borderRadius: 1
};

const emptyMaterial: Material = {
  id: 0,
  level1: '',
  level2: '',
  level3: '',
  level4: '',
  isActive: true,
  isDeleted: false
};

const AddNewMaterialPopUp: React.FC<MaterialFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  material,
  isEdit
}) => {
  const theme = useTheme();
  const [form, setForm] = useState<Material>(emptyMaterial);
  const [errors, setErrors] = useState<Partial<Record<keyof Material, string>>>({});

  // Load material into form when dialog opens
  useEffect(() => {
    if (open) {
      setForm(material ?? emptyMaterial);
      setErrors({});
    }
  }, [open, material]);

  const handleChange = (field: keyof Material, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const v: Partial<Record<keyof Material, string>> = {};

    if (!form.level1) v.level1 = "Level 1 is required";
    if (!form.level2) v.level2 = "Level 2 is required";

    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            <Category /> {isEdit ? 'Update Material' : 'Add New Material'}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
        <Paper elevation={0} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Level 1"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level1}
                onChange={(e) => handleChange('level1', e.target.value)}
                error={Boolean(errors.level1)}
                helperText={errors.level1}
              />

              <TextField
                fullWidth
                label="Level 3"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level3}
                onChange={(e) => handleChange('level3', e.target.value)}
                error={Boolean(errors.level3)}
                helperText={errors.level3}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Level 2"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level2}
                onChange={(e) => handleChange('level2', e.target.value)}
                error={Boolean(errors.level2)}
                helperText={errors.level2}
              />

              <TextField
                fullWidth
                label="Level 4"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level4}
                onChange={(e) => handleChange('level4', e.target.value)}
                error={Boolean(errors.level4)}
                helperText={errors.level4}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="Active Material"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button
          variant="contained"
          sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }
              }}
          onClick={handleSubmit}
        >
          {isEdit ? 'Update Material' : 'Create Material'}
        </Button>

        <Button
          variant="contained"
          sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }
              }}
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewMaterialPopUp;